import React from 'react';
import PropTypes from 'prop-types';
import {
  Box, Text,
} from 'grommet';
import ReactTooltip from 'react-tooltip';
import { useSpring, animated } from '@react-spring/web';
import { useAppTheme } from '@Components/Context';
import {
  SIDENAV_WIDTH,
  SIDENAV_COLLAPSED_WIDTH,
} from '../constants';
import {
  StyledMenuItem,
  StyledSVG,
  StyledAuthBanner,
  StyledBannerHeader,
} from './StyledAuthNavWrapper';

export const AuthBanner = ({
  small,
  title = null,
  subTitle = null,
  navCollapsed,
  bannerChildren = null,
  bannerChildrenPosition = 'bottom',
  adjustLeftPad = false,
  smallBanner = true,
  showPaymentNotice = null,
}) => {
  const { theme: { navigation } } = useAppTheme();
  const bottomBannerChildren = bannerChildren && bannerChildrenPosition === 'bottom';
  const rightBannerChildren = bannerChildren && bannerChildrenPosition === 'right';
  const padValue = '1.5rem';
  let padLeft = navCollapsed ? `calc(${padValue} + ${SIDENAV_COLLAPSED_WIDTH})` : `calc(${padValue} + ${SIDENAV_WIDTH})`;
  let padTop = small ? '2.5rem' : '3rem';
  let padBottom = '1.25rem';
  let textSize = small ? '2rem' : '1.75rem';

  if (adjustLeftPad) padLeft = padValue;

  if (smallBanner) {
    padTop = small ? '2rem' : '4.65rem';
    padBottom = small ? '2rem' : '0.65rem';
    textSize = small ? '1.85rem' : '1.4rem';
  }

  if (small) {
    padLeft = '1.5rem';
  }

  return (
    <StyledAuthBanner
      fill
      data-bg={navigation.bannerBg}
      background={{ image: `url(${navigation.bannerBg})` }}
      style={{ backgroundColor: 'var(--nav-banner-bg-color)' }}
      pad={{
        bottom: padBottom,
        top: padTop,
        right: padValue,
        left: padLeft,
      }}
      className="lazyload fade-out"
    >
      <Box flex direction={rightBannerChildren ? 'row' : 'column'} justify={rightBannerChildren ? 'between' : 'start'}>
        <Box gap="0.5rem" direction="column">
          {subTitle && (
            <Text
              uppercase
              size={smallBanner ? '0.75rem' : '1rem'}
              color={navigation.bannerTextColor}
              style={{ letterSpacing: '3px' }}
            >
              {subTitle}
            </Text>
          )}
          <StyledBannerHeader margin="none" size={textSize} weight={600} color={navigation.bannerTextColor}>
            {title ?? 'Loading...'}
          </StyledBannerHeader>
        </Box>
        {rightBannerChildren && (
          <Box direction="column" justify="center">
            {bannerChildren}
          </Box>
        )}
      </Box>
      {showPaymentNotice && (
        <Box pad={{ top: '0.65rem' }}>
          {showPaymentNotice}
        </Box>
      )}
      {bottomBannerChildren && (
        <Box direction="column" pad={{ top: '0.65rem' }}>
          {bannerChildren}
        </Box>
      )}
    </StyledAuthBanner>
  );
};

AuthBanner.propTypes = {
  small: PropTypes.bool.isRequired,
  title: PropTypes.string,
  subTitle: PropTypes.string,
  navCollapsed: PropTypes.bool.isRequired,
  bannerChildren: PropTypes.node,
  bannerChildrenPosition: PropTypes.oneOf(['bottom', 'right']),
  adjustLeftPad: PropTypes.bool,
  smallBanner: PropTypes.bool,
  showPaymentNotice: PropTypes.node,
};

export const MenuSectionHeader = ({
  label,
  textColor,
  collapsed,
}) => {
  const {
    opacity, display, y, maxHeight,
  } = useSpring({
    opacity: collapsed ? 0 : 1,
    display: collapsed ? 'none' : 'block',
    y: collapsed ? -10 : 0,
    maxHeight: collapsed ? '0px' : '53px',
    delay: collapsed ? 0 : 100,
    config: { mass: 0.5, tension: 300, friction: 35 },
  });

  return (
    <animated.div
      style={{
        opacity,
        display,
        y,
        maxHeight,
      }}
    >
      <Box pad={{ vertical: '0.15rem' }}>
        <Text size="0.9rem" weight={600} color={textColor}>
          {label}
        </Text>
      </Box>
    </animated.div>
  );
};

MenuSectionHeader.propTypes = {
  label: PropTypes.string.isRequired,
  textColor: PropTypes.string.isRequired,
  collapsed: PropTypes.bool.isRequired,
};

export const MenuItemRenderer = ({
  location,
  renderKey,
  first = false,
  disabled = false,
  label,
  path = null,
  pathMatch = null,
  collapsed,
}) => {
  const {
    theme: { page, button },
    icons,
  } = useAppTheme();

  const selectIcon = (key) => {
    switch (key) {
      case 'all-infringements':
        return icons.allInfringementsIcon;
      case 'confirmed-removals':
        return icons.confirmedRemovalsIcon;
      case 'all-searches':
        return icons.searchesIcon;
      case 'piracy':
        return icons.piracyFlagIcon;
      case 'sentiment':
        return icons.sentimentHeartIcon;
      case 'reports':
        return icons.customReportsIcon;
      case 'analytics':
        return icons.cyclopsAnalyticsIcon;
      case 'live-events':
        return icons.cyclopsLiveEventsIcon;
      case 'past-events':
        return icons.cyclopsPastEventsIcon;
      case 'dmca-allowlist':
        return icons.allowListIcon;
      case 'dmca-denylist':
        return icons.denyListIcon;
      case 'dmca-author_allowlist':
        return icons.authorAllowIcon;
      case 'review_denylist':
        return icons.reviewDenyIcon;
      case 'review-deny_platforms':
        return icons.reviewDenyPlatformIcon;
      case 'teams-settings':
        return icons.companyUnitsIcon;
      case 'units-settings':
        return icons.companyUnitsIcon;
      case 'content-id-asset-upload':
        return icons.claimsUploadIcon;
      case 'content-id-assets':
        return icons.listAssetsIcon;
      case 'content-id-allowlist':
        return icons.allowListIcon;
      case 'content-id-claims':
        return icons.listClaimsIcon;
      case 'fingerprinting-settings':
        return icons.fingerprintingSettingsIcon;
      case 'live-tracker':
        return icons.liveTrackerIcon;
      case 'content-id-referrals':
        return icons.creatorReferralsIcon;
      case 'cid-analytics':
        return icons.youtubeIcon;
      case 'cid-payout-setup':
        return icons.listAssetsIcon;
      case 'custom-onboarding':
        return icons.creatorReferralsIcon;
      case 'dmca-isp_allowlist':
        return icons.ispAllowlistIcon;
      default:
        return icons.settingsIcon;
    }
  };

  const { opacity, display } = useSpring({
    opacity: collapsed ? 0 : 1,
    display: collapsed ? 'none' : 'block',
    delay: collapsed ? 0 : 100,
  });
  const isActive = typeof pathMatch === 'function'
    ? pathMatch(location.pathname)
    : location.pathname === path;
  let useIconColor = isActive ? button.iconHoverColor : button.iconColor;
  if (disabled) useIconColor = button.iconDisabledColor;
  const iconSize = collapsed ? '1rem' : '0.9rem';

  return (
    <StyledMenuItem
      disabled={disabled}
      disabledText={button.iconDisabledColor}
      path={path}
      pathMatch={pathMatch}
      buttonHoverColor={button.hoverBg}
      buttonHighlight={button.primaryBg}
      primaryText={page.primaryText}
      highlightText={button.hoverText}
      iconHighlight={button.iconHoverColor}
      first={first}
      className={`tour-${renderKey}`}
      data-for={`${renderKey}-tooltip`}
      data-tip={label}
    >
      <Box
        direction="row"
        gap={collapsed ? '0' : '0.5rem'}
        align="center"
        justify={collapsed ? 'center' : 'start'}
        pad={{ left: collapsed ? '0' : '0.5rem' }}
        height="1.75rem"
        width="100%"
      >
        <Box
          justify="center"
          align="center"
          height={iconSize}
          width={iconSize}
          pad={{ top: collapsed ? '0.25rem' : '0.15rem' }}
        >
          <StyledSVG
            className="highlightIcon"
            src={selectIcon(renderKey)}
            height={iconSize}
            width={iconSize}
            $fillColor={useIconColor}
          />
        </Box>
        {collapsed && (
          <ReactTooltip
            id={`${renderKey}-tooltip`}
            className="sideNavTip"
            arrowColor={button.primaryBg}
            place="right"
            effect="solid"
            delayShow={200}
            opacity={1}
            style={{
              backgroundColor: button.primaryBg,
              color: button.primaryText,
              zIndex: 9999,
            }}
          />
        )}
        <animated.div style={{ opacity, display }}>
          <Box height="1.75rem" justify="center">
            <Text size="0.85rem" weight={500}>
              {label}
            </Text>
          </Box>
        </animated.div>
      </Box>
    </StyledMenuItem>
  );
};

MenuItemRenderer.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  renderKey: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  path: PropTypes.string,
  pathMatch: PropTypes.func,
  collapsed: PropTypes.bool.isRequired,
  first: PropTypes.bool,
  disabled: PropTypes.bool,
};
